import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import { mapyCzLayers } from '@/features/shared/tileLayers';
import type { GeoPoint } from '@/signalr/contracts';
import type { MapMarker } from './markers';
import { LeafletMapView } from './LeafletMapView';
import { LeafletMapWithPopups } from './LeafletMapWithPopups';

export type IncidentListMapComponentProps = {
  center?: GeoPoint | null;
  zoom?: number;
  items?: readonly MapMarker[];
  disableControls?: boolean;
  onMarkerClicked: (markerId: string) => void | Promise<void>;
  popupContent?: ReactNode;
};

type MapConfigurationDto = {
  defaultPosition: GeoPoint;
  defaultZoom: number;
  mapyCzApiKey: string;
};

export function useAgencyMapConfiguration(): MapConfigurationDto {
  const agencyConfiguration = useAgencyConfiguration();
  return agencyConfiguration.agencyPortalConfiguration.mapConfiguration as MapConfigurationDto;
}

export function IncidentListMapComponent({
  center,
  zoom,
  items,
  disableControls = false,
  onMarkerClicked,
  popupContent,
}: IncidentListMapComponentProps) {
  const { t, i18n } = useTranslation();
  const mapConfiguration = useAgencyMapConfiguration();

  const language = (i18n.language || 'en').split('-')[0] ?? 'en';
  const tileLayers = useMemo(
    () => mapyCzLayers(language, mapConfiguration.mapyCzApiKey, t),
    [language, mapConfiguration.mapyCzApiKey, t],
  );

  return (
    <LeafletMapWithPopups
      mapContent={
        <LeafletMapView
          center={center}
          zoom={zoom}
          markers={items}
          disableControls={disableControls}
          tileLayers={tileLayers}
          onMarkerClicked={onMarkerClicked}
        />
      }
      popupContent={popupContent}
    />
  );
}
